import bridge from '../offline/bridge';

// The SpeechRecognition used inside the React Native app. react-speech-recognition (and so
// SpeechToText.js / FormFieldMic) talk to this class exactly as they talk to the browser API.
// Never installed in a normal browser.
//
// It drives one of two engines:
//  1. the WebView's own webkitSpeechRecognition, when the WebView has one (Android System WebView
//     does: it records through the Google app's recognizer and also works offline), or
//  2. the native module (expo-speech-recognition) over the RPC bridge (stt.*), when the WebView has
//     no engine or reports that its recognition service is unavailable.
//
// Why the WebView engine is wrapped instead of handed to the library as-is: on Android, Chromium
// reports every partial result of a continuous session as "final" with confidence 0, and OFFLINE
// results never carry a confidence score at all. react-speech-recognition keeps an Android final
// only when its confidence is > 0 — so offline nothing was ever kept and each new phrase overwrote
// the previous one. This class turns both engines into clean interim/final results and carries
// un-finalised phrases forward (see _handleResult / _handleEnd).

const DEFAULT_LANG = 'en-US'; // native-module engine only; the WebView engine keeps its own default
export const NATIVE_SPEECH_ERROR_EVENT = 'nativeSpeechError';

const IS_ANDROID = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent || '');

// Native-module engine: expected during normal use — no toast, the library may restart listening.
const SILENT_ERRORS = new Set(['aborted', 'no-speech', 'speech-timeout']);
// WebView engine: only these end dictation with a message. Every other error keeps the behaviour
// that engine always had in the app — the library simply starts listening again.
const WEBVIEW_FATAL_ERRORS = new Set(['not-allowed', 'audio-capture']);

const ERROR_MESSAGES = {
  'not-allowed': 'Microphone permission is required for voice typing. Please allow it in the app settings.',
  'service-not-allowed': 'Speech recognition is not available on this device.',
  'language-not-supported': 'This language is not available for voice typing on this device.',
  'offline-model-missing':
    'Offline voice typing is not set up yet. Connect to the internet once and reopen the app to download the speech pack.',
  network: 'Voice typing could not reach the speech service. Check your connection and try again.',
  'audio-capture': 'The microphone is busy or unavailable.',
  'engine-stuck': 'Voice typing could not start. Please tap the mic again.',
};

// Android's recognizer is not reliable about confirming what it was asked to do (seen on a device:
// a session restarted 2 ms after the previous one ended reported `start`, never opened the microphone
// and then ignored stop()). Nothing below may ever wait on the engine without a time limit.
const RESTART_GAP_MS = 250; // let Android release the previous recognizer before the next session
const ENGINE_ALIVE_MS = 2000; // `start` seen but no audio by then (normally ~150 ms): the session is hung
const STOP_CONFIRM_MS = 1500; // a stop that the engine does not confirm by then is completed here
const MAX_ENGINE_ATTEMPTS = 3;
// Offline, the first words of a phrase can take ~2 s to arrive after the engine hears speech. The
// mic buttons use this to avoid auto-stopping in the middle of that wait (bounded by this cap).
export const MAX_SILENCE_WAIT_MS = 8000;

// A gap this long since the recognised WORDS last changed means the speaker paused. A recognizer
// only starts a new phrase after its own end-of-speech silence (~1.8 s in field logs), while
// corrections of the current phrase arrive far faster. Measured on word changes, not on events:
// recognizers keep sending empty or repeated partials during silence, which must not hide the pause.
const PAUSE_MS = 700;

const joinText = (a, b) => [a, b].map((s) => (s || '').trim()).filter(Boolean).join(' ');
// Punctuation ignored when comparing phrases (ASCII, Latin-1, general and CJK punctuation).
// Deliberately NOT /\p{P}/u: Babel ships that syntax untouched, it is a parse error in old browsers
// and WebViews, and this file is part of the start-up bundle of the website and the app.
const PUNCTUATION = /[\u0021-\u002F\u003A-\u0040\u005B-\u0060\u007B-\u007E\u00A1-\u00BF\u2010-\u2027\u2030-\u205E\u3000-\u303F]/g;
const words = (s) => s.toLowerCase().replace(PUNCTUATION, '').split(/\s+/).filter(Boolean);
const sameWords = (a, b) => words(a).join(' ') === words(b).join(' ');

// Is `next` a different phrase from the un-finalised `pending`, rather than a longer/corrected
// version of it?
//  - Text collapsing to under half its length is always a new phrase.
//  - A FINAL normally just polishes the pending partial (punctuation, a corrected word), so it is
//    a new phrase only when it keeps none of its words, or keeps few AND has a different length.
//  - A partial without a pause is a revision. After a pause the recognizer either keeps growing
//    the same text (`next` still begins with the pending words, at most the last one corrected)
//    or has started over: anything else is a new phrase and must be appended, never replace.
function isNewPhrase(pending, next, { paused, isFinal }) {
  const a = words(pending);
  const b = words(next);
  if (!a.length || !b.length) return false;
  if (next.length < pending.length * 0.5) return true;
  if (isFinal) {
    const kept = a.filter((w) => b.includes(w)).length;
    if (kept === 0) return true;
    // A rewording keeps roughly the same number of words; a new phrase does not.
    return kept / a.length < 0.5 && Math.abs(a.length - b.length) >= 2;
  }
  if (!paused) return false;
  let common = 0;
  while (common < a.length && common < b.length && a[common] === b[common]) common += 1;
  return !(b.length >= a.length && common >= Math.max(1, a.length - 1));
}

let _counter = 0;
let _current = null; // the instance that owns the running native-module session
let _subscribed = false;
let WebViewRecognition = null; // the WebView's own engine constructor, when it has one
let webViewEngineUnavailable = false; // it exists, but reported that its service is unavailable
let lastEngineEndAt = 0; // when the WebView engine last ended / was let go of
let speechInProgress = false; // the engine currently hears speech it has not turned into text yet

/** True while the engine reports speech in progress (always false on the website). */
export const isNativeSpeechInProgress = () => speechInProgress;

const ENGINE_EVENTS = ['onstart', 'onaudiostart', 'onsoundstart', 'onspeechstart', 'onspeechend', 'onsoundend', 'onaudioend', 'onnomatch'];

function subscribeOnce() {
  if (_subscribed) return;
  _subscribed = true;
  bridge.on('stt.activity', (p) => {
    if (p?.event === 'speechstart') speechInProgress = true;
    else if (p?.event === 'speechend' || p?.event === 'audioend') speechInProgress = false;
  });
  bridge.on('stt.result', (p) => _current && _current._handleBridgeResult(p || {}));
  bridge.on('stt.error', (p) => _current && _current._handleBridgeError(p || {}));
  bridge.on('stt.end', (p) => _current && _current._handleBridgeEnd(p || {}));
}

function announceError(error, message) {
  try {
    window.dispatchEvent(new CustomEvent(NATIVE_SPEECH_ERROR_EVENT, {
      detail: { error, message: ERROR_MESSAGES[error] || message || 'Voice typing failed. Please try again.' },
    }));
  } catch (e) { /* ignore */ }
}

export default class NativeBridgeSpeechRecognition {
  constructor() {
    this.continuous = false;
    this.interimResults = true;
    this.lang = '';
    this.onresult = null;
    this.onend = null;
    this.onerror = null;
    this._sessionId = null;
    this._webview = null; // live WebView-engine object of the current session, if that engine is used
    this._engine = null; // 'webview' | 'native-module' for the current session
    this._stopping = false; // stop() was requested and the engine has not confirmed it yet
    this._attempt = 0; // engine starts tried for the current session
    this._startTimer = null;
    this._aliveTimer = null;
    this._stopTimer = null;
    this._lastWasFinal = false;
    this._pendingInterim = ''; // interim text the recognizer has not finalised yet
    this._carry = ''; // earlier un-finalised phrases kept in front of the current one
    this._lastChangeAt = 0; // when the recognised words last changed
    subscribeOnce();
  }

  start() {
    if (this._sessionId) {
      if (!this._stopping) return Promise.resolve(); // already listening
      // The previous session was asked to stop, the engine has not confirmed it yet, and dictation
      // is being started again: let go of it now instead of swallowing this start. Its text is
      // already in the input, so it is dropped rather than committed (no duplicates).
      this._releaseEngine();
      this._sessionId = null;
      this._pendingInterim = '';
      this._carry = '';
      this._stopping = false;
      this._emitEnd(); // lets the library clear its "stopped" state before this start completes
      if (this._sessionId) return Promise.resolve(); // the library restarted from inside onend
    }
    const sessionId = `stt-${Date.now()}-${++_counter}`;
    this._sessionId = sessionId;
    this._pendingInterim = '';
    this._carry = '';
    this._stopping = false;
    this._attempt = 0;
    // _lastWasFinal is deliberately kept across sessions: the library's duplicate-final filter
    // also survives its own continuous-mode restarts.
    _current = this;
    this._engine = WebViewRecognition && !webViewEngineUnavailable ? 'webview' : 'native-module';
    if (this._engine === 'native-module') return this._startBridge(sessionId);
    this._scheduleWebViewStart(sessionId);
    // Resolves at once (the library marks itself "listening" and can be stopped normally) while
    // the engine itself may be started a moment later — see RESTART_GAP_MS.
    return Promise.resolve();
  }

  stop() {
    if (!this._sessionId) return;
    if (this._stopping) return; // already stopping; the first request's time limit still runs
    this._stopping = true;
    clearTimeout(this._aliveTimer);
    const sessionId = this._sessionId;
    if (this._engine === 'webview' && !this._webview) {
      // The engine start is still pending (restart gap / retry): there is nothing to wait for.
      clearTimeout(this._startTimer);
      this._startTimer = null;
      setTimeout(() => { if (this._sessionId === sessionId) this._handleEnd(); }, 0);
      return;
    }
    if (this._webview) {
      try { this._webview.stop(); } catch (e) { /* not running */ }
    } else {
      bridge.call('stt.stop').catch(() => {});
    }
    // The session must end even if the engine never confirms the stop; otherwise this object would
    // stay "listening" forever and every later start would be ignored.
    this._stopTimer = setTimeout(() => {
      if (this._sessionId !== sessionId) return;
      this._releaseEngine();
      this._handleEnd();
    }, STOP_CONFIRM_MS);
  }

  abort() {
    if (!this._sessionId) return;
    // Drop the session now so late engine events for it are ignored, and end locally.
    this._releaseEngine();
    this._sessionId = null;
    this._pendingInterim = '';
    this._carry = '';
    this._stopping = false;
    setTimeout(() => this._emitEnd(), 0);
  }

  // Let go of whatever engine session exists, without waiting for it and without ending the
  // dictation session itself. Late events from a released WebView engine object are ignored.
  _releaseEngine() {
    clearTimeout(this._startTimer);
    clearTimeout(this._aliveTimer);
    clearTimeout(this._stopTimer);
    this._startTimer = null;
    this._aliveTimer = null;
    this._stopTimer = null;
    speechInProgress = false;
    const rec = this._webview;
    this._webview = null;
    if (rec) {
      ['onresult', 'onerror', 'onend', ...ENGINE_EVENTS].forEach((name) => { rec[name] = null; });
      // abort() (unlike stop()) ends the engine session on Chromium's side without needing an
      // answer from Android's recognizer.
      try { rec.abort(); } catch (e) { /* not running */ }
      lastEngineEndAt = Date.now();
    } else if (this._engine === 'native-module') {
      bridge.call('stt.abort').catch(() => {});
    }
  }

  // ---------------------------------------------------------------- engine 1: the WebView's own

  _scheduleWebViewStart(sessionId) {
    const wait = Math.max(0, RESTART_GAP_MS - (Date.now() - lastEngineEndAt));
    if (wait === 0) {
      this._startWebView(sessionId);
      return;
    }
    this._startTimer = setTimeout(() => {
      this._startTimer = null;
      if (this._sessionId === sessionId && !this._stopping) this._startWebView(sessionId);
    }, wait);
  }

  // `start` arrived but the microphone never opened: the engine session is hung. Replace it with a
  // fresh one for the SAME dictation session (kept text and phrases are untouched).
  _onEngineStuck(sessionId, rec) {
    if (this._sessionId !== sessionId || this._webview !== rec || this._stopping) return;
    this._attempt += 1;
    this._releaseEngine();
    if (this._attempt >= MAX_ENGINE_ATTEMPTS) {
      this._commitPendingInterim();
      this._fail(sessionId, 'engine-stuck');
      return;
    }
    this._scheduleWebViewStart(sessionId);
  }

  _startWebView(sessionId) {
    let rec;
    try {
      rec = new WebViewRecognition();
      rec.continuous = !!this.continuous;
      rec.interimResults = true;
      if (this.lang) rec.lang = this.lang; // otherwise the WebView's default, as it always was
    } catch (e) {
      webViewEngineUnavailable = true;
      this._engine = 'native-module';
      this._startBridge(sessionId);
      return;
    }
    this._webview = rec;
    const mine = () => this._sessionId === sessionId && this._webview === rec;
    const engineIsAlive = () => { clearTimeout(this._aliveTimer); this._aliveTimer = null; };
    let switchToModule = false;

    rec.onresult = (event) => {
      if (!mine()) return;
      engineIsAlive();
      const results = event?.results || [];
      const from = typeof event?.resultIndex === 'number' ? event.resultIndex : results.length - 1;
      for (let i = Math.max(0, from); i < results.length; i += 1) {
        const alt = results[i]?.[0];
        if (!alt || typeof alt.transcript !== 'string') continue;
        const confidence = typeof alt.confidence === 'number' ? alt.confidence : 0;
        const rawFinal = !!results[i].isFinal;
        // Chromium on Android flags every partial of a continuous session as final with
        // confidence 0; only a real final carries a score. Offline there is never a score — those
        // phrases are kept by the pause / end-of-session handling instead.
        const isFinal = rawFinal && (!IS_ANDROID || confidence > 0);
        this._handleResult({ transcript: alt.transcript, confidence, isFinal });
      }
    };

    rec.onerror = (event) => {
      if (!mine()) return;
      engineIsAlive();
      const code = event?.error || 'unknown';
      if (code === 'service-not-allowed') {
        // The API is there but no recognition service is behind it: use the native module.
        webViewEngineUnavailable = true;
        switchToModule = true;
        return;
      }
      if (WEBVIEW_FATAL_ERRORS.has(code)) this._announceFatal(code, event?.message);
    };

    rec.onend = () => {
      if (!mine()) return;
      engineIsAlive();
      this._webview = null;
      lastEngineEndAt = Date.now();
      speechInProgress = false;
      if (switchToModule) {
        this._engine = 'native-module';
        this._startBridge(sessionId);
        return;
      }
      this._handleEnd();
    };

    ENGINE_EVENTS.forEach((name) => {
      rec[name] = () => {
        if (!mine()) return;
        const event = name.slice(2);
        if (event === 'start') {
          // Chromium reports `start` before Android's recognizer has actually opened the mic.
          clearTimeout(this._aliveTimer);
          this._aliveTimer = setTimeout(() => this._onEngineStuck(sessionId, rec), ENGINE_ALIVE_MS);
        } else {
          engineIsAlive();
        }
        if (event === 'soundstart' || event === 'speechstart') speechInProgress = true;
        else if (event === 'speechend' || event === 'soundend' || event === 'audioend') speechInProgress = false;
      };
    });

    try {
      rec.start();
    } catch (e) {
      this._webview = null;
      setTimeout(() => this._fail(sessionId, 'unknown', e?.message), 0);
    }
  }

  // ---------------------------------------------------------------- engine 2: the native module

  _startBridge(sessionId) {
    // Always resolves: the library must finish its own start bookkeeping before it sees onend.
    return bridge
      .call('stt.start', { sessionId, lang: this.lang || DEFAULT_LANG, continuous: !!this.continuous })
      .then(() => {})
      .catch((err) => {
        if (this._sessionId !== sessionId) return;
        setTimeout(() => this._fail(sessionId, err?.code || 'unknown', err?.message), 0);
      });
  }

  _isCurrentBridgeEvent(payload) {
    return !!this._sessionId && !this._webview && payload.sessionId === this._sessionId;
  }

  _handleBridgeResult(payload) {
    if (!this._isCurrentBridgeEvent(payload) || typeof payload.transcript !== 'string') return;
    this._handleResult(payload);
  }

  _handleBridgeError(payload) {
    if (!this._isCurrentBridgeEvent(payload)) return;
    const code = payload.error || 'unknown';
    if (SILENT_ERRORS.has(code)) return;
    this._announceFatal(code, payload.message); // the native 'end' follows
  }

  _handleBridgeEnd(payload) {
    if (!this._isCurrentBridgeEvent(payload)) return;
    this._handleEnd();
  }

  // ---------------------------------------------------------------- shared by both engines

  _handleResult({ transcript: rawTranscript, confidence: rawConfidence, isFinal: rawIsFinal }) {
    const isFinal = !!rawIsFinal;
    // A recognizer can begin a new phrase after a pause without ever finalising the previous one
    // (always the case offline); the library would then overwrite the earlier words. When the
    // incoming text is a new phrase rather than a revision of the pending one, the old words are
    // carried in front of everything that follows until the next final. (They are not sent as a
    // separate final: the library debounces finals on Android and would drop the next one.)
    const text = rawTranscript.trim();
    const now = Date.now();
    // Recognizers send empty results at phrase boundaries and re-send the same partial while the
    // speaker is silent. Neither carries new words: ignoring them keeps the remembered text intact
    // and keeps the pause measurable.
    if (!text) return;
    if (!isFinal && text === this._pendingInterim) return;
    if (this._pendingInterim) {
      const paused = now - this._lastChangeAt >= PAUSE_MS;
      if (isNewPhrase(this._pendingInterim, text, { paused, isFinal })) {
        this._carry = joinText(this._carry, this._pendingInterim);
      }
    }
    // Re-punctuation / re-casing of the same words is not new speech: do not restart the pause clock.
    if (!this._pendingInterim || !sameWords(this._pendingInterim, text)) this._lastChangeAt = now;
    this._pendingInterim = isFinal ? '' : text;
    const transcript = joinText(this._carry, rawTranscript);
    if (isFinal) this._carry = '';
    // react-speech-recognition (Android UA) drops a final that directly follows another final as a
    // duplicate — put the same text through as an interim first.
    if (isFinal && this._lastWasFinal) this._emitResult(transcript, 0, false);
    // …and it only accepts finals whose confidence is > 0.
    const confidence = isFinal ? (rawConfidence > 0 ? rawConfidence : 1) : (rawConfidence || 0);
    this._emitResult(transcript, confidence, isFinal);
    this._lastWasFinal = isFinal;
  }

  _commitPendingInterim() {
    const text = joinText(this._carry, this._pendingInterim);
    this._pendingInterim = '';
    this._carry = '';
    if (!text) return;
    this._emitResult(text, 1, true);
    this._lastWasFinal = true;
  }

  _emitResult(transcript, confidence, isFinal) {
    if (typeof this.onresult !== 'function') return;
    const result = [{ transcript, confidence }];
    result.isFinal = isFinal;
    this.onresult({ results: [result], resultIndex: 0 });
  }

  // Stop the library's continuous auto-restart and tell the UI why dictation ended.
  _announceFatal(code, message) {
    this.continuous = false;
    announceError(code, message);
    // 'not-allowed' is deliberately NOT forwarded: the library would disable recognition for the
    // rest of the session, even after the user grants the permission.
    if (code !== 'not-allowed' && typeof this.onerror === 'function') this.onerror({ error: code });
  }

  _handleEnd() {
    clearTimeout(this._startTimer);
    clearTimeout(this._aliveTimer);
    clearTimeout(this._stopTimer);
    this._startTimer = null;
    this._aliveTimer = null;
    this._stopTimer = null;
    this._stopping = false;
    speechInProgress = false;
    // Session ended (pause/timeout/stop) with words still un-finalised: keep them before any restart.
    this._commitPendingInterim();
    this._sessionId = null;
    this._emitEnd();
  }

  _fail(sessionId, code, message) {
    if (this._sessionId !== sessionId) return;
    this._releaseEngine();
    this._sessionId = null;
    this._stopping = false;
    this.continuous = false;
    if (!SILENT_ERRORS.has(code)) announceError(code, message);
    this._emitEnd();
  }

  _emitEnd() {
    if (typeof this.onend === 'function') this.onend();
  }
}

/** Called once at install time with the WebView's own engine constructor (or null). */
export function setWebViewRecognition(ctor) {
  WebViewRecognition = typeof ctor === 'function' && ctor !== NativeBridgeSpeechRecognition ? ctor : null;
}
